import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Stickyard from 'stickyard';
import memoize from 'memoize-one';
import SearchBox from '@adsk/bim360-matrix-react-search-box';
import { sortableContainer } from 'react-sortable-hoc';

import ColumnsToggleItem from './ColumnsToggleItem';

import './_ColumnsToggle.scss';

function getItems(_columns, groups, unGroupedText) {
  const columns = _columns.filter(column => !column.locked);
  if (!groups || !groups.length) return columns;

  let items = [];
  groups.forEach(group => {
    const children = columns.filter(column => column.groupKey === group.key);
    if (children.length) {
      items.push({ ...group, isHeader: true });
      items = items.concat(children);
    }
  });

  const groupKeys = groups.map(group => group.key);
  const ungrouped = columns.filter(column => !groupKeys.includes(column.groupKey));
  if (ungrouped.length) {
    items.push({ key: '__ungrouped__', title: unGroupedText, isHeader: true });
    items = items.concat(ungrouped);
  }

  return items;
}

const getFilteredItems = memoize((columns, groups, unGroupedText, searchExpression) => {
  const search = searchExpression.toLowerCase();
  const filteredColumns = columns.filter(
    column => column.title && column.title.toLowerCase().indexOf(search) > -1
  );
  return getItems(filteredColumns, groups, unGroupedText);
});

const DragableContainer = sortableContainer(({ children }) => children);

class ColumnsToggle extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      searchExpression: ''
    };

    this._handleChange = this._handleChange.bind(this);
    this._handleClear = this._handleChange.bind(this, '');
    this._handleDragEnd = this._handleDragEnd.bind(this);
    this._setToggleRef = this._setToggleRef.bind(this);

    // do nothing for onSearch but it's required for SearchBox
    this._handleSearch = () => {};
  }

  render() {
    const {
      className,
      style,
      columns,
      groups,
      freezable,
      unGroupedText,
      noColumnsFoundText,
      searchColumnsPlaceholderText,
      onToggle,
      onFrozen,
      columnsReorderable
    } = this.props;
    const { searchExpression } = this.state;
    const items = getFilteredItems(columns, groups, unGroupedText, searchExpression);
    const draggable = columnsReorderable && !searchExpression && (!groups || !groups.length);
    return (
      <div className={cn('ColumnsToggle', className)} style={style} ref={this._setToggleRef}>
        <SearchBox
          className="ColumnsToggle__search"
          onInputRefMount={this._handleSearchboxMount}
          searchExpression={searchExpression}
          onChange={this._handleChange}
          onClear={this._handleClear}
          onSearch={this._handleSearch}
          placeholder={searchColumnsPlaceholderText}
        />
        <Stickyard>
          {({ registerContainer, registerSticky }) => (
            <DragableContainer
              useDragHandle
              onSortEnd={this._handleDragEnd}
              helperContainer={() => this.toggleRef}
              helperClass="ColumnsToggle__drag-container"
            >
              <div className="ColumnsToggle__menu" ref={registerContainer}>
                {items.map(item =>
                  item.isHeader ? (
                    <div key={item.key} ref={registerSticky} className="ColumnsToggle__header">
                      {item.title}
                    </div>
                  ) : (
                    <ColumnsToggleItem
                      key={`${item.key}-${item.position || 0}`}
                      column={item}
                      freezable={freezable}
                      onToggle={onToggle}
                      onFrozen={onFrozen}
                      draggable={draggable}
                    />
                  )
                )}
              </div>
            </DragableContainer>
          )}
        </Stickyard>
        {items.length === 0 && (
          <div className="ColumnsToggle__no-results">{noColumnsFoundText}</div>
        )}
      </div>
    );
  }

  _setToggleRef(node) {
    this.toggleRef = node;
  }

  _handleDragEnd({ oldIndex, newIndex }) {
    if (oldIndex === newIndex) return;
    const { columns, onReorder } = this.props;
    const source = columns.find(col => col.position === oldIndex);
    const target = columns.find(col => col.position === newIndex);
    onReorder(source, target);
  }

  _handleSearchboxMount(searchBox) {
    searchBox.focus();
  }

  _handleChange(searchExpression) {
    this.setState({ searchExpression });
  }
}

ColumnsToggle.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  columns: PropTypes.arrayOf(PropTypes.object),
  groups: PropTypes.arrayOf(PropTypes.object),
  freezable: PropTypes.bool,
  unGroupedText: PropTypes.string,
  noColumnsFoundText: PropTypes.string,
  searchColumnsPlaceholderText: PropTypes.string,
  onToggle: PropTypes.func,
  onFrozen: PropTypes.func,
  onReorder: PropTypes.func,
  columnsReorderable: PropTypes.bool
};

export default ColumnsToggle;
